import type { LearnBrandConfig } from './types'
import { deepMerge } from './deep-merge'
import { defaults } from './defaults'
import { overrides as acpOverrides } from './brands/acp'

const brandOverrides: Record<string, Record<string, unknown>> = {
  acp: acpOverrides as Record<string, unknown>,
}

let cached: LearnBrandConfig | null = null

export function getBrandId(): string {
  return import.meta.env.VITE_BRAND_ID || 'acp'
}

/**
 * Fields with no neutral meaning: a brand that omits one would render as, or be
 * named after, whichever brand happened to be the baseline. Checked at resolve
 * time because the SPA has no server to fail at.
 */
const REQUIRED: string[] = [
  'id',
  'name',
  'learn.footer.es',
  'learn.footer.en',
  'assets.logo',
  'theme.googleFontsUrl',
]

function missingPaths(cfg: unknown): string[] {
  const get = (path: string) =>
    path.split('.').reduce<unknown>((o, k) => (o as Record<string, unknown> | undefined)?.[k], cfg)
  return REQUIRED.filter((path) => {
    const v = get(path)
    return v === undefined || v === null || v === ''
  })
}

/**
 * A brand problem in a browser is worse than on a server: there is no application
 * ErrorBoundary above the theme import, so an uncaught throw during module
 * evaluation renders a blank page with nothing said. And because VITE_BRAND_ID is
 * inlined at BUILD time, it cannot be fixed by changing an env var and
 * restarting — it needs a rebuild. So say what is wrong, in the page, before
 * rethrowing.
 */
function explainInPage(message: string): void {
  if (typeof document === 'undefined') return
  const host = document.getElementById('root') ?? document.body
  if (!host) return
  const pre = document.createElement('pre')
  pre.style.cssText =
    'margin:0;padding:24px;font:13px/1.6 ui-monospace,monospace;white-space:pre-wrap;color:#1b1e24;background:#f4f5f7'
  pre.textContent = `Brand configuration error\n\n${message}`
  host.replaceChildren(pre)
}

export function getBrand(): LearnBrandConfig {
  if (cached) return cached
  const id = getBrandId()
  const overrides = brandOverrides[id]

  if (!overrides) {
    const msg =
      `Unknown VITE_BRAND_ID '${id}'. Registered brands: ${Object.keys(brandOverrides).join(', ')}.\n` +
      `An unregistered id used to resolve to the default brand's entire configuration ` +
      `in silence, so a typo shipped another brand's name and palette.`
    explainInPage(msg)
    throw new Error(msg)
  }

  const resolved = deepMerge(
    defaults as unknown as Record<string, unknown>,
    overrides as unknown as Record<string, unknown>,
  ) as unknown as LearnBrandConfig

  const missing = missingPaths(resolved)
  if (missing.length) {
    const msg =
      `Brand '${id}' is missing ${missing.length} required field(s):\n` +
      missing.map((m) => `  ${m}`).join('\n') +
      `\n\nFill them in at src/brand/brands/${id}.ts.`
    explainInPage(msg)
    throw new Error(msg)
  }

  cached = resolved
  return cached
}

export type { LearnBrandConfig } from './types'
